import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useFilters } from '@/context/FiltersContext';
import { FuelTypeFilter } from './FuelTypeFilter';
import { RadiusSlider } from '@/components/search/RadiusSlider';
import { BrandFilter } from './BrandFilter';
import { AmenityFilter } from './AmenityFilter';

interface FilterPanelProps {
  className?: string;
  stations?: Array<{ brand: string; amenities: string[] }>;
}

const STORAGE_KEY = 'pumpscout_filter_panel_expanded';

export function FilterPanel({ className, stations = [] }: FilterPanelProps) {
  const isMobile = useIsMobile();
  const { setAvailableBrands } = useFilters();

  // Load expanded state from localStorage
  const [isExpanded, setIsExpanded] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });

  // Persist expanded state
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(isExpanded));
    } catch {
      // Ignore storage errors
    }
  }, [isExpanded]);

  // Memoize brands to prevent infinite loop
  const brands = useMemo(() => {
    return [...new Set(stations.map((s) => s.brand))].sort();
  }, [stations]);

  // Track previous brands to avoid unnecessary updates
  const prevBrandsRef = useRef<string>('');

  // Extract unique brands from stations and update context
  useEffect(() => {
    const brandsKey = brands.join(',');
    if (brandsKey !== prevBrandsRef.current) {
      prevBrandsRef.current = brandsKey;
      setAvailableBrands(brands);
    }
  }, [brands, setAvailableBrands]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev: boolean) => !prev);
  }, []);

  // Mobile sheet
  if (isMobile) {
    return (
      <>
        {/* Collapsed bar */}
        <div className={clsx('bg-white rounded-xl shadow-lg', className)}>
          <button
            type="button"
            onClick={toggleExpanded}
            className="w-full px-4 py-3 flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <FilterIcon className="w-5 h-5 text-gray-500" />
              <span className="font-medium text-gray-900">Filters</span>
            </div>
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <ChevronIcon className="w-5 h-5 text-gray-400" />
            </motion.div>
          </button>
        </div>

        {/* Mobile sheet overlay */}
        <AnimatePresence>
          {isExpanded && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.5 }}
                exit={{ opacity: 0 }}
                onClick={toggleExpanded}
                className="fixed inset-0 bg-black z-40"
              />

              {/* Sheet */}
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-xl max-h-[80vh] overflow-hidden"
              >
                {/* Drag handle */}
                <div className="flex justify-center pt-3 pb-2">
                  <div className="w-10 h-1 bg-gray-300 rounded-full" />
                </div>

                {/* Header */}
                <div className="flex items-center justify-between px-4 pb-3 border-b border-gray-100">
                  <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                  <button
                    type="button"
                    onClick={toggleExpanded}
                    className="p-1 text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Content */}
                <div className="overflow-y-auto p-4 space-y-6" style={{ maxHeight: 'calc(80vh - 80px)' }}>
                  <FuelTypeFilter />
                  <RadiusSlider />
                  <BrandFilter stations={stations} />
                  <AmenityFilter stations={stations} />
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </>
    );
  }

  // Desktop sidebar
  return (
    <div className={clsx('bg-white rounded-xl shadow-lg overflow-hidden', className)}>
      {/* Toggle button */}
      <button
        type="button"
        onClick={toggleExpanded}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <FilterIcon className="w-5 h-5 text-gray-500" />
          <span className="font-medium text-gray-900">Filters</span>
        </div>
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronIcon className="w-5 h-5 text-gray-400" />
        </motion.div>
      </button>

      {/* Expandable content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 space-y-5 border-t border-gray-100 pt-4">
              <FuelTypeFilter />
              <RadiusSlider showTicks={false} />
              <BrandFilter stations={stations} />
              <AmenityFilter stations={stations} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Icon components
function FilterIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
      />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}
